import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowUpRight, ChevronRight, FlaskConical, Home, MapPin, Syringe } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { dtpPath, useDtpParams } from '../../lib/clinicSite';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { formatDate, LabRequisitionType, PatientLabOrder } from '../../lib/patientSelfService';

/**
 * Lab orders on their way to a result: a tracker per open order on the
 * Results page, and a compact card for the portal home.
 */

/** Still moving, or stuck and worth knowing about. Ready and cancelled ones aren't "in progress". */
export const isOpenLabOrder = (order: PatientLabOrder) => order.state === 'in_progress' || order.state === 'needs_attention';

export function useLabOrders() {
  const { physicianId } = useDtpParams();
  const { isAuthenticated, patient } = useDtpAuthStore();
  return useQuery({
    queryKey: ['patient-lab-orders', physicianId, patient?._id],
    enabled: isAuthenticated,
    retry: false,
    gcTime: 0,
    queryFn: (): Promise<PatientLabOrder[]> => dtpApi.getLabOrders(),
  });
}

const TYPE: Record<LabRequisitionType, { label: string; Icon: typeof Home }> = {
  at_home_kit: { label: 'At-home kit', Icon: Home },
  walk_in: { label: 'Lab visit', Icon: MapPin },
  mobile_phleb: { label: 'Mobile blood draw', Icon: Syringe },
};

export function LabOrderTracker({ order }: { order: PatientLabOrder }) {
  const { physicianId } = useDtpParams();
  const type = TYPE[order.requisitionType] || TYPE.at_home_kit;
  const current = order.steps[order.currentStep];
  return <article aria-labelledby={`lab-order-${order.id}`} className="portal-card p-5 sm:p-6">
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <span aria-hidden="true" className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[var(--portal-tint-3)]"><FlaskConical className="h-5 w-5" /></span>
        <div className="min-w-0">
          <h3 id={`lab-order-${order.id}`} className="portal-display text-lg font-semibold">{order.panelName}</h3>
          <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-sm text-[var(--portal-muted)]">
            <span className="inline-flex items-center gap-1"><type.Icon aria-hidden="true" className="h-3.5 w-3.5" />{type.label}</span>
            {order.orderedAt && <span>· Ordered {formatDate(order.orderedAt)}</span>}
          </p>
        </div>
      </div>
      {order.state === 'needs_attention'
        ? <span className="inline-flex rounded-full bg-[#fde4dc] px-2.5 py-1 text-xs font-semibold text-[#8c2a14]">Needs attention</span>
        : <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--portal-tint-1)] px-2.5 py-1 text-xs font-semibold text-[var(--portal-ink)]">
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-current opacity-70 motion-safe:animate-pulse" />{current?.label}
        </span>}
    </header>

    {order.state === 'needs_attention' ? (
      <p className="mt-4 rounded-2xl bg-white/70 p-3 text-sm ring-1 ring-[var(--portal-line)]">The lab couldn’t accept this order as placed. Your clinic has been told and will be in touch; you don’t need to do anything yet.</p>
    ) : (
      <ol aria-label={`Lab order progress: ${current?.label || ''}`} className="mt-5 grid grid-cols-4 gap-1.5">
        {order.steps.map((step, index) => <li key={step.label} aria-current={index === order.currentStep ? 'step' : undefined} className="min-w-0">
          <span aria-hidden="true" className={`block h-1.5 rounded-full ${step.reached ? 'bg-[var(--portal-accent)]' : 'bg-[rgba(36,26,21,0.08)]'}`} />
          <span className={`mt-2 block text-[11px] font-medium leading-tight sm:text-xs ${step.reached ? 'text-[var(--portal-ink)]' : 'text-[var(--portal-muted)]'}`}>{step.label}</span>
          {step.at && <span className="mt-0.5 block text-[11px] text-[var(--portal-muted)]">{formatDate(step.at, { month: 'short', day: 'numeric' })}</span>}
        </li>)}
      </ol>
    )}

    {(order.nextStep || order.fasting || (order.turnaroundDays && order.state === 'in_progress')) && <ul className="mt-4 space-y-1.5 text-sm text-[var(--portal-muted)]">
      {order.nextStep && <li>{order.nextStep}</li>}
      {order.fasting && <li><strong className="font-semibold text-[var(--portal-ink)]">Fasting required.</strong> Follow the fasting instructions from your clinic or the lab before you give your sample.</li>}
      {order.turnaroundDays && order.state === 'in_progress' ? <li>Results usually arrive about {order.turnaroundDays} {order.turnaroundDays === 1 ? 'day' : 'days'} after the lab receives your sample.</li> : null}
    </ul>}

    {order.resultId && <Link to={dtpPath(physicianId, `/results/${order.resultId}`)} className="mt-5 inline-flex h-10 items-center gap-2 rounded-full bg-[var(--portal-primary)] px-4 text-sm font-semibold text-[var(--portal-primary-fg)] hover:opacity-90">View results<ArrowUpRight aria-hidden="true" className="h-4 w-4" /></Link>}
  </article>;
}

/** "In progress" for the Results page; renders nothing when no lab order is open. */
export function LabOrdersInProgressSection() {
  const labOrders = useLabOrders();
  const open: PatientLabOrder[] = (labOrders.data || []).filter(isOpenLabOrder);
  // A failed lookup stays quiet: the results list below is the page's job,
  // and an error box above it for a secondary panel would read as "your
  // results failed to load".
  if (!open.length) return null;
  return <section aria-labelledby="labs-in-progress" className="mt-8">
    <h2 id="labs-in-progress" className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--portal-muted)]">In progress</h2>
    <div className="mt-3 space-y-3">{open.map(order => <LabOrderTracker key={order.id} order={order} />)}</div>
  </section>;
}

/** Compact home card: open lab orders and where each one is. Hidden when there are none. */
export function LabOrdersInProgress() {
  const { physicianId } = useDtpParams();
  const labOrders = useLabOrders();
  const open: PatientLabOrder[] = (labOrders.data || []).filter(isOpenLabOrder);
  if (!open.length) return null;
  return <section aria-labelledby="home-labs-in-progress" className="portal-card p-6 sm:p-7">
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--portal-tint-3)]"><FlaskConical aria-hidden="true" className="h-[18px] w-[18px]" /></span>
        <h2 id="home-labs-in-progress" className="portal-display text-lg font-semibold">Labs in progress</h2>
      </div>
      <Link to={dtpPath(physicianId, '/results')} className="inline-flex h-9 items-center gap-1 rounded-full px-3 text-sm font-medium text-[var(--portal-muted)] transition hover:bg-[rgba(36,26,21,0.05)] hover:text-[var(--portal-ink)]">Track<ChevronRight aria-hidden="true" className="h-4 w-4" /></Link>
    </div>
    <ul className="mt-5 space-y-3">
      {open.slice(0, 3).map(order => {
        const step = order.steps[order.currentStep];
        return <li key={order.id} className="rounded-2xl bg-white/70 p-4 ring-1 ring-[var(--portal-line)]">
          <p className="truncate font-semibold">{order.panelName}</p>
          <p className="mt-1 text-sm text-[var(--portal-muted)]">{order.state === 'needs_attention' ? 'Needs attention — your clinic will be in touch' : `${step?.label} · step ${order.currentStep + 1} of ${order.steps.length}`}</p>
          {order.state !== 'needs_attention' && <span aria-hidden="true" className="mt-3 block h-1.5 overflow-hidden rounded-full bg-[rgba(36,26,21,0.08)]">
            <span className="block h-full rounded-full bg-[var(--portal-accent)]" style={{ width: `${((order.currentStep + 1) / order.steps.length) * 100}%` }} />
          </span>}
        </li>;
      })}
    </ul>
  </section>;
}
